import type { ReactNode } from 'react';
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { headingStyle } from '../theme/headingTheme';

const useStyles = makeStyles({
  section: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    rowGap: tokens.spacingVerticalL,
    columnGap: tokens.spacingHorizontalXXXL,
    padding: tokens.spacingHorizontalXXL,
    '@media (min-width: 1201px)': {
      gridTemplateColumns: '240px minmax(0, 1fr)',
    },
  },
  divided: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    '&:last-child': { borderBottom: 'none' },
  },
  heading: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: tokens.spacingVerticalS,
  },
  title: {
    ...headingStyle,
    margin: 0,
    fontSize: tokens.fontSizeBase400,
  },
  description: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    '& p': { margin: 0 },
  },
  body: {
    minWidth: 0,
  },
});

interface FormSectionProps {
  id?: string;
  title: string;
  divider?: boolean;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}

const FormSection = ({
  id,
  title,
  divider = true,
  description,
  actions,
  children,
}: FormSectionProps) => {
  const styles = useStyles();
  return (
    <section
      id={id}
      aria-label={title}
      className={mergeClasses(styles.section, divider && styles.divided)}
    >
      <header className={styles.heading}>
        <h3 className={styles.title}>{title}</h3>
        {description && <div className={styles.description}>{description}</div>}
        {actions}
      </header>
      <div className={styles.body}>{children}</div>
    </section>
  );
};

export default FormSection;
